import React, {useState, useCallback, useEffect} from 'react';
import Base from './base.jsx';
import {fetchJson} from '../../../helpers/index.js';
import {Mode, DataType, castDataType} from '../common.jsx';
import Helpers from './helpers.jsx'
import Setting from './setting.jsx';
import {FaIcon} from '../../common/Icons.jsx';
import {Portal} from '../../common/Portal.jsx';
import {closeModal, Modal as ModalComponent} from '../../common/Modal.jsx';
import FileUploadUppy from '../../common/uppy_upload/FileUploadUppy.lazy';
import Errors from '../../common/Errors.jsx';
import LoadingIndicator from '../../common/loading_indicator.jsx';
import {Button} from '../../bootstrap/index.jsx';

import excelParserWorker from '@workers/excelParserWorker.js?worker';
import useGridState from '../../datagrid/import_wizard/useGridState.js';
import useSteps from '../../datagrid/import_wizard/useSteps.jsx';
import {buildCellClassRules, buildValueGetter} from '../../datagrid/import_wizard/dynamicValueHelpers.js';
import {ColumnErrorNavigation} from '../../datagrid/import_wizard/ImportWizardModal.jsx';
import {getInitialColumnDefs} from '../../datagrid/import_wizard/settings.js';
import {readExcelSheetNames} from '../../datagrid/import_wizard/readExcel.js';
import Grid from '../../datagrid/import_wizard/Grid.jsx';
import styles from '../../datagrid/import_wizard/ImportWizard.module.scss';

import {
  datagrid_excel_import_selectable_configurations_path,
  datagrid_excel_import_config_show_path,
  datagrid_get_component_props_json_path,
} from '~/routes';

const MODAL_ID = 'dg-excel-import-comp-modal';

export default class AdaliaMeDatagridExcelImporter extends Base {
  static get CATEGORY() {
    return 'question';
  }

  static get TYPE() {
    return 'adalia_me_datagrid_excel_importer';
  }

  static icon() {
    return <i className='fa fa-file-import'></i>;
  }

  static get TRANSLATED_ATTRIBUTES() {
    return {
      label: 'field_default_label',
      description: 'field_default_description',
    };
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      name: 'field_' + Helpers.randomString(),
      label: 'label',
      description: 'description',
      help: 'help',
      reportKey: '',
      required: true,
      label_hidden: false,
      configurationErrors: [
        {name: 'required', argument: 'importConfigurationId', path: []},
      ]
    });
  }

  static disallowNesting() {
    return true;
  }

  constructor(props) {
    super(props);
    this.openModal = this.openModal.bind(this);
    this.handleCloseModal = this.handleCloseModal.bind(this);

    this.state = {
      selectableConfigurations: [],
      dgProps: null,
      xlsConfig: null,
      showModal: false,
    };
    this.gridRef = React.createRef();
  }

  componentDidMount() {
    super.componentDidMount();

    if (this.props.mode === Mode.SETTINGS) {
      jQuery.getJSON(datagrid_excel_import_selectable_configurations_path(), (res) => {
        this.setState({selectableConfigurations: res})
      })

    } else if ([Mode.FILL, Mode.VIEW].includes(this.props.mode)) {
      const {importConfigurationId} = this.props.content;
      if (importConfigurationId) {
        fetchJson(datagrid_excel_import_config_show_path({id: importConfigurationId, project_id: this.context.projectId, _options: true})).then(xlsConfig => {
          fetchJson(datagrid_get_component_props_json_path({datagrid_id: xlsConfig.datagrid_id, programme_form_id: this.context.programmeFormId, project_id: this.context.projectId, _options: true})).then(dgProps => {
            xlsConfig.config.settings.range_to = Infinity;
            this.setState({xlsConfig: xlsConfig, dgProps: dgProps})
          })
        })
      }
    }
  }

  openModal() {
    this.setState({showModal: true})
  }

  handleCloseModal() {
    this.setState({showModal: false});
    closeModal(MODAL_ID);
  }

  renderView() {
    const answer = this.getAnswer();
    const isEditMode = (this.props.mode === Mode.FILL);

    return (
      <div style={{display: 'flex', flexDirection: 'column'}}>
        <label className={this.props.content.label_hidden ? 'hidden-label' : ''}>
          <span className='field-label'>{this.getText('label')} *</span>
          {this.renderPrefillIcon()}
        </label>
        {this.descriptionText()}

        {[Mode.FILL, Mode.EDIT].includes(this.props.mode) && <>

          {this.renderHiddenInputs(answer)}
        </>}

        {this.renderProcessingSummary(answer, isEditMode)}

        {(this.state.dgProps && this.state.xlsConfig && this.state.showModal) &&
          <DataGridImportPreview {...{
            gridRef: this.gridRef,
            dgProps: this.state.dgProps,
            xlsConfig: this.state.xlsConfig,
            value: answer,
            updateAnswer: this.updateAnswer,
            fieldName: this.props.content.name,
            saveAnswerBlobUrl: this.props.saveAnswerBlobUrl,
            permissions: this.props.imagePermissions,
            handleCloseModal: this.handleCloseModal,
            isEditMode: isEditMode,
          }}/>
        }
      </div>
    );
  }

  renderProcessingSummary(answer, isEditMode) {
    const {total_row_count = 0, path = null} = answer || {};
    const showButton = isEditMode || total_row_count > 0;

    return (
      <div className='d-flex rounded p-1 mt-1' style={{border: '2px solid #00000033'}}>
        <div className='d-flex' style={{alignItems: 'center'}}>
          <span>{I18n.t('rows_imported_from_excel_file', {count: total_row_count})}</span>
        </div>

        {(this.props.mode !== Mode.SETTINGS) && (
          showButton && (
            <a className='btn btn btn-sm btn-default' onClick={this.openModal} style={{marginLeft: 'auto'}}>
              {isEditMode ? I18n.t('import_from_excel') : I18n.t('click_to_view')}
            </a>
          )
        )}
        {path && (
          <a className='btn btn btn-sm btn-link' href={path}>
            {I18n.t('download_excel')}
          </a>
        )}
      </div>
    );
  }

  renderHiddenInputs(answer) {
    return Object.keys(answer || {}).map(prop => {
      if (typeof answer[prop] != 'object') {
        return <input
          type='hidden'
          name={`${this.fullFieldName(false)}[${prop}]`}
          value={answer[prop]}
          key={`[${prop}]-${answer.id}`}
        />
      }
      return Object.keys(answer[prop]||[]).map(nestedProp => {
        return <input
          type='hidden'
          name={`${this.fullFieldName(false)}[${prop}][${nestedProp}]`}
          value={answer[prop][nestedProp]}
          key={`[${prop}][${nestedProp}]-${answer.id}`}
        />
      })
    })
  }

  renderSettings() {
    return (
      <div>
        <Setting.TextField
          name="label"
          label={I18n.t('field_label', {scope: 'fe'})}
          value={this.getText("label")}
          onChange={this.updateSettings}
        />
        <Setting.Checkbox
          name="label_hidden"
          className="label-hidden-check"
          label={I18n.t('field_label_hide', {scope: 'fe'})}
          value={this.props.content.label_hidden}
          onChange={this.updateSettings}
        />
        <Setting.LongTextField
          name="description"
          label={I18n.t('field_description', {scope: 'fe'})}
          value={this.getText("description")}
          onChange={this.updateSettings}
        />
        {this.state.selectableConfigurations.length > 0 &&
          <Setting.Dropdown
            name='importConfigurationId'
            label={I18n.t('field_excel_import_configuration', {scope: 'fe'})}
            description={this.state.selectableConfigurations.length === 0 ? I18n.t('no_configurations_defined', {scope: 'fe'}) : null}
            value={this.props.content.importConfigurationId}
            onChange={this.updateSettings}
            options={this.state.selectableConfigurations.map(d => [d.id, d.name])}
            includeBlankOption={true}
            {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
          />
        }
        <Setting.TextField
          name="reportKey"
          label={I18n.t('field_report_key', {scope: 'fe'})}
          value={this.props.content.reportKey}
          description={I18n.t('field_report_key_description', {scope: 'fe'})}
          componentType={this.constructor.TYPE}
          {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
        />
      </div>
    );
  }

  beforeUpdateSettings(content) {
    content.label_hidden = castDataType(content.label_hidden, DataType.BOOLEAN);
    content.importConfigurationId = castDataType(content.importConfigurationId, DataType.INTEGER);
    const {datagrid_id} = this.state.selectableConfigurations.find((v) => v.id === content.importConfigurationId)||{};
    content.datagridId = datagrid_id || null;
  }
}

const DataGridImportPreview = ({gridRef, dgProps, xlsConfig, value, updateAnswer, fieldName, saveAnswerBlobUrl, permissions = {}, handleCloseModal, isEditMode}) => {
  const [answer, setAnswer] = useState(value);
  const [processingExcel, setProcessingExcel] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fileSizeError, setFileSizeError] = useState(false);
  const [rowsWithErrors, setRowsWithErrors] = useState([]);
  const [errors, setErrors] = useState([]);

  const {datagrid, context} = dgProps;
  const {settings, settings: {ws_name}, xls_settings} = xlsConfig.config;

  const {toggleGrid, gridState} = useGridState(isEditMode ? null : 'datagrid', gridRef);
  const {steps} = useSteps(
    gridRef,
    datagrid,
    settings,
    () => {},
    xls_settings,
    fileSizeError,
    setFileSizeError,
    () => {},
    context,
    toggleGrid,
  )

  const handleFileUpload = (res) => {
    const gridApi = gridRef.current.api;
    gridApi.setGridOption('columnDefs', []);
    gridApi.setGridOption('rowData', []);

    if (res.file) {
      setProcessingExcel(true);
      const {file, ...rest} = res;
      const fileData = file.data;

      readExcelSheetNames(fileData).then(_ws_names => {
        processingExcelFile(fileData, rest);
      })
    } else {
      gridApi.setGridOption('rowData', []);
      gridApi.applyTransactionAsync({add: []});

      setAnswer(null);
      setProcessingExcel(false);
    }
  }

  const processingExcelFile = (file, res) => {
    const gridApi = gridRef.current.api;

    // Create a new Web Worker
    const worker = excelParserWorker({name: 'excel-parser-worker'});

    let totalRowCount = 0;
    let totalColCount = 0
    worker.onmessage = (e) => {
      const {type} = e.data;

      if (type === 'columns') {
        const colNames = e.data.col_names;
        totalColCount = (colNames || []).length;
        let columnDefs = getInitialColumnDefs(datagrid, settings, isEditMode ? colNames : [], isEditMode);

        // Add the names from the selected header row to colDef headerNames
        let excelColIndex = 0;
        columnDefs = columnDefs.map(colDef => {
          if (!colDef.field.includes('-e')) return colDef;
          colDef.headerName = xls_settings.ws_column_names[excelColIndex].label;
          excelColIndex += 1;
          return colDef;
        });
        gridApi.setGridOption('columnDefs', columnDefs);

        // Reset row count
        totalRowCount = 0;

      } else if (type === 'rows') {
        const rows = e.data.rows;
        totalRowCount += rows.length;
        gridApi.applyTransactionAsync({add: rows});

      } else if (type === 'done') {
        // Parsed rows, skipping blanks
        setProcessingExcel(false)
        setAnswer({...res, total_row_count: totalRowCount});
        worker.terminate();

      } else if (type === 'error') {
        console.error('Worker error:', e.data.error);
        setProcessingExcel(false)
        worker.terminate();
      }
    };

    // Send the file to the web worker for processing
    worker.postMessage({file, ws_name});
  }

  useEffect(() => {
    const gridApi = gridRef.current?.api;
    const colDefs = gridApi?.getGridOption('columnDefs');
    if (!colDefs || !answer) return;

    // Update the value getters to ag-grid columnDefs
    datagrid.columns.forEach((column) => {
      const columnImportConfig = settings.columns[column.accessor] || {};

      let colDefs = gridApi.getGridOption('columnDefs');
      colDefs.forEach(colDef => {
        if (colDef.field === column.accessor) { // Selected column
          colDef.valueGetter = buildValueGetter(column, columnImportConfig);
          colDef.cellClassRules = buildCellClassRules(column, columnImportConfig)
        }
      })
      gridApi.setGridOption('columnDefs', colDefs)
    })

    if (isEditMode) toggleGrid('excel');
  }, [answer])

  const validateColumns = (colDefs) => {
    let rowsWithErrorIds = [];
    let range_from_idx = parseInt(settings.range_from || 1) - 1;

    gridRef.current.api.forEachNode((rowNode, index) => {
      if (range_from_idx > index) return;
      let isRowAlreadyFlagged = false;

      colDefs.forEach(colDef => {
        if (isRowAlreadyFlagged) return; // Already found an error in this row

        const validationRule = colDef.cellClassRules?.['import-cell-error'];
        const blankRule = colDef.cellClassRules?.['import-cell-blank'];
        if (typeof validationRule !== 'function' && typeof blankRule !== 'function') return;

        const value = colDef.valueGetter({data: rowNode.data});
        if ((typeof blankRule === 'function' && blankRule({value})) || (typeof validationRule === 'function' && validationRule({value}))) {
          rowsWithErrorIds.push(rowNode.id);
          isRowAlreadyFlagged = true;
        }
      })
    })

    setRowsWithErrors(rowsWithErrorIds);
  }

  const onGridReady = (params) => {
    if (_.isEmpty(answer)) return; //_.isEqual(value, answer)

    // Load the uploaded Excel file, if it exists
    const loadExcelFile = async () => {
      const res = await fetch(answer.path);
      const blob = await res.blob();
      const file = new File([blob], answer.metadata.filename, {type: blob.type});
      handleFileUpload({...answer, file: {data: file}})
    };

    loadExcelFile();
  }

  const onAsyncTransactionsFlushed = useCallback((event) => {
    const accessors = datagrid.columns.map(c => c.accessor)
    validateColumns(event.api.getGridOption('columnDefs').filter(c => accessors.includes(c.field)));
  }, [])

  const handleSave = (e) => {
    e.preventDefault();

    const gridApi = gridRef.current.api;
    const columnDefs = gridApi.getColumnDefs();
    if (!columnDefs) return;

    setSaving(true);

    const columns = datagrid.columns.map((c) => c.accessor);
    const answer_blob = {
      datagrid_id: datagrid.id,
      cols: columns,
      rows: (() => {
        const rows = [];
        gridApi.forEachNode(node => {
          const row = {};
          for (const accessor of columns) {
            row[accessor] = gridApi.getCellValue({rowNode: node, colKey: accessor});
          }
          rows.push(row);
        });
        return rows;
      })()
    }

    fetchJson(
      saveAnswerBlobUrl,
      {
        method: 'POST',
        body: JSON.stringify({
          field_name: fieldName,
          answer_data: {
            answer,
            answer_blob
          },
        })
      }
    ).then(res => {
      if (res.errors) {
        setErrors(res.errors);
      } else {
        setErrors([]);

        const {attachment_id, path, total_row_count} = answer;
        const {filename, size} = answer.metadata;
        updateAnswer({attachment_id, path, metadata: {filename, size}, total_row_count, answer_blob_id: res.id});
        handleCloseModal();
      }
      setSaving(false);
    })
  }

  return (
    <Portal
      id={MODAL_ID}
      size='xl'
      title={isEditMode ? I18n.t('import') : I18n.t('view')}
      cancelLabel={I18n.t('close')}
      onClose={handleCloseModal}
      onSave={handleSave}
      saving={saving}
      hideSaveButton={!isEditMode}
      disableSave={(rowsWithErrors.length > 0)}
      cssClassNameCancelButton={isEditMode ? 'pull-left' : 'pull-right'}
      component={ModalComponent}
    >
      <Errors errors={errors}/>

      {isEditMode &&
        <div className='form-group' style={{width: '500px'}}>
          <label>{I18n.t('upload_excel_file')} *</label>
          <FileUploadUppy
            allowedFileTypes={['.xlsx']}
            maxFileSize={2000000}
            maxNumberOfFiles={1}
            height={200}
            uploadCallback={handleFileUpload}
            includeFileInCallback={true}
            permissions={permissions || {}}
            isForm={true}
            showRemoveButtonAfterComplete={true}
          />
        </div>
      }

      {isEditMode && <>
        <div className='btn-group mb-1'>
          <Button size='sm' type={gridState === 'excel' ? 'primary' : 'default'} onClick={() => toggleGrid('excel')}>
            <FaIcon type='file-excel-o'/> {I18n.t('excel')}
          </Button>
          <Button size='sm' type={gridState === 'datagrid' ? 'primary' : 'default'} onClick={() => toggleGrid('datagrid')}>
            <FaIcon type='table'/> {I18n.t('grid')}
          </Button>
        </div>
        <ColumnErrorNavigation validationErrors={rowsWithErrors} gridRef={gridRef}/>
      </>}

      {processingExcel && <LoadingIndicator/>}
      <div style={{ display: processingExcel ? 'none' : 'block'}}>
        <Grid
          {...{
            gridRef,
            settings,
            gridOptions: {
              onGridReady: onGridReady,
              onAsyncTransactionsFlushed: onAsyncTransactionsFlushed
            }
          }}
          className={styles['grid']}
          customStyles={{height: isEditMode ? 'calc(55vh - 100px)' :  'calc(60vh)'}}
        />
      </div>
    </Portal>
  )
};
